import { isNotNull } from '@racemap/utilities/functions/validation';
import type { Bounds } from '@racemap/utilities/types/geos';
import type {
  Feature,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
} from 'geojson';
import type { Immutable } from 'immer';
import { getInverseBounds } from './getMaxBounds';

/**
 * Computes the smallest axis-aligned bounding box that contains all coordinates
 * from the given GeoJSON features. Only `Point` and `LineString` geometries are
 * considered; other geometry types are ignored. Returns `null` when no valid
 * coordinates are found.
 *
 * @param features - An array of GeoJSON features whose coordinates are used to compute the bounds.
 * @returns The smallest {@link Bounds} enclosing all coordinates, or `null` if the features contain no valid points.
 */
export function getBounds(
  features: Immutable<
    Array<Feature<
      LineString | MultiLineString | Point | MultiPoint | Polygon | MultiPolygon
    > | null>
  >,
): Bounds | null {
  const points = features
    .filter(isNotNull)
    .map((f) => {
      const { geometry } = f;
      if (geometry == null) {
        return [];
      }
      if (geometry.type === 'Point') {
        return [geometry.coordinates];
      }
      if (geometry.type === 'LineString') {
        return geometry.coordinates;
      }
      return [];
    })
    .reduce((r, a = []) => r.concat(a), [])
    .filter((r) => r.length > 1);

  if (points.length === 0) return null;

  const bounds = getInverseBounds();
  for (let i = 0, len = points.length; i < len; i++) {
    const [lng, lat] = points[i];
    bounds.minLat = Math.min(lat, bounds.minLat);
    bounds.maxLat = Math.max(lat, bounds.maxLat);
    bounds.minLng = Math.min(lng, bounds.minLng);
    bounds.maxLng = Math.max(lng, bounds.maxLng);
  }
  return bounds;
}
